import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { getParcels } from '../api/parcels';
import { getMeasurements } from '../api/measurements';
import { getChatSessions } from '../api/chat';
import { StatCard } from '../components/common/Card';
import { FullPageSpinner } from '../components/common/Spinner';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';

export default function Dashboard() {
  const [parcels, setParcels] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getParcels(),
      getMeasurements(),
      getChatSessions().catch(() => ({ data: [] })),
    ])
      .then(([pRes, mRes, cRes]) => {
        setParcels(pRes.data?.results ?? pRes.data);
        const m = mRes.data?.results ?? mRes.data;
        setMeasurements(m.slice(0, 20));
        setChatSessions(cRes.data?.results ?? cRes.data ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <FullPageSpinner />;

  const chartData = [...measurements]
    .reverse()
    .map((m) => ({
      date: format(new Date(m.measured_at), 'MM/dd HH:mm'),
      moisture: parseFloat(m.soil_moisture ?? 0).toFixed(1),
      ph: parseFloat(m.ph_value ?? 0).toFixed(1),
      temp: parseFloat(m.soil_temperature ?? 0).toFixed(1),
    }));

  const activeSessions = chatSessions.filter((s) => s.initialized);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Контролна табла</h1>
        <p className="text-gray-500 text-sm mt-1">Преглед на вашата земјоделска платформа</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Вкупно парцели"
          value={parcels.length}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          }
        />
        <StatCard
          label="Мерења"
          value={measurements.length}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
        <StatCard
          label="Активни AI разговори"
          value={activeSessions.length}
          color="yellow"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
        <StatCard
          label="Вкупна површина (м²)"
          value={parcels.reduce((sum, p) => sum + parseFloat(p.size_m2 || 0), 0).toFixed(0)}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4">Последни мерења</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="moisture" stroke="#16a34a" name="Влажност %" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="ph" stroke="#3b82f6" name="pH" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="temp" stroke="#f59e0b" name="Темп °C" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              Сè уште нема податоци за мерења.{' '}
              <Link to="/measurements" className="ml-1 text-green-600 underline">Додај мерења</Link>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4">Брзи акции</h2>
          <div className="space-y-3">
            <Link to="/parcels/new" className="block">
              <Button className="w-full" size="sm">+ Додај парцела</Button>
            </Link>
            <Link to="/chat" className="block">
              <Button variant="outline" className="w-full" size="sm">Отвори AI разговор</Button>
            </Link>
            <Link to="/measurements" className="block">
              <Button variant="outline" className="w-full" size="sm">Додај мерење</Button>
            </Link>
            <Link to="/imports" className="block">
              <Button variant="outline" className="w-full" size="sm">Увоз / Извоз на податоци</Button>
            </Link>
          </div>
          {parcels.length === 0 && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Започнете со додавање парцела, потоа користете AI разговор за анализа на вашите полиња.
            </p>
          )}
        </div>
      </div>

      {parcels.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-700">Вашите парцели</h2>
            <Link to="/parcels" className="text-sm text-green-700 hover:underline">Прегледај ги сите</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Име</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Култура</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Големина</th>
                  <th className="text-left py-2 text-gray-500 font-medium">AI Статус</th>
                  <th className="text-left py-2 text-gray-500 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {parcels.slice(0, 5).map((p) => {
                  const session = chatSessions.find((s) => s.parcel === p.id);
                  return (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2">
                        <Link to={`/parcels/${p.id}`} className="text-green-700 font-medium hover:underline">
                          {p.parcel_name}
                        </Link>
                      </td>
                      <td className="py-2">
                        <Badge color="green">{p.crop_type || '—'}</Badge>
                      </td>
                      <td className="py-2 text-gray-600">{p.size_m2} м²</td>
                      <td className="py-2">
                        {session?.initialized ? (
                          <Badge color="green">Анализирано</Badge>
                        ) : (
                          <Badge color="gray">Не анализирано</Badge>
                        )}
                      </td>
                      <td className="py-2">
                        <Link to={`/chat?parcel=${p.id}`} className="text-sm text-green-600 hover:underline">
                          {session?.initialized ? 'Продолжи разговор' : 'Почни анализа'}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
